import { collectTopicKeys, evaluateTrigger } from '@wit-support/pipeline';
/**
 * Above this many distinct topics the exhaustive check (2^n label sets) is
 * skipped and `triggersOverlap` answers `true`. Over-reporting an overlap only
 * shows a spurious «Срабатывает на те же письма, что и …» hint; under-reporting
 * would hide a real double start, so the conservative side is «overlaps».
 * Prod triggers mention ≤ 8 topics combined, far below the cap (2^16 = 65 536).
 */
export const MAX_OVERLAP_TOPICS = 16;
/**
 * EXACT check: is there a set of classified labels on which both triggers fire?
 *
 * Both expressions depend only on the topics they mention, so enumerating every
 * subset of the union of mentioned keys (the empty set included — `always` and
 * a bare `not` are true on it, matching `evaluateTrigger`) covers every
 * possible classification. `null` means «manual only» and never fires
 * automatically, so it overlaps with nothing.
 */
export const triggersOverlap = (a, b) => {
    if (a === null || b === null)
        return false;
    // Symmetric, so the pair is keyed order-independently.
    const ja = JSON.stringify(a);
    const jb = JSON.stringify(b);
    const key = ja < jb ? `${ja}\u0000${jb}` : `${jb}\u0000${ja}`;
    const cached = overlapCache.get(key);
    if (cached !== undefined)
        return cached;
    const result = computeOverlap(a, b);
    if (overlapCache.size >= OVERLAP_CACHE_LIMIT)
        overlapCache.clear();
    overlapCache.set(key, result);
    return result;
};
/**
 * Pair results by the JSON of the two triggers. The list page recomputes every
 * pair (n²/2, each up to 2^16 evaluations) on every render that touches the
 * list; triggers rarely change, so almost every call is a repeat. Keyed by
 * VALUE, not identity: a refetch hands back new objects with the same triggers.
 * Bounded — cleared wholesale when full (the working set is a few hundred pairs).
 */
const overlapCache = new Map();
const OVERLAP_CACHE_LIMIT = 5000;
const computeOverlap = (a, b) => {
    const keys = [...new Set([...collectTopicKeys(a), ...collectTopicKeys(b)])];
    if (keys.length > MAX_OVERLAP_TOPICS)
        return true;
    const total = 1 << keys.length;
    for (let mask = 0; mask < total; mask++) {
        const labels = new Set(keys.filter((_, i) => (mask & (1 << i)) !== 0));
        if (evaluateTrigger(a, labels) && evaluateTrigger(b, labels))
            return true;
    }
    return false;
};
/**
 * For every ENABLED pipeline, the keys of the other enabled pipelines whose
 * triggers can fire on the same mail (in input order). Disabled pipelines are
 * ignored on both sides; pipelines with no overlap get no entry, so callers read
 * `map.get(key) ?? []`.
 */
export const overlappingPipelines = (list) => {
    const enabled = list.filter((p) => p.enabled);
    const result = new Map();
    for (let i = 0; i < enabled.length; i++) {
        for (let j = i + 1; j < enabled.length; j++) {
            const a = enabled[i];
            const b = enabled[j];
            if (!triggersOverlap(a.trigger, b.trigger))
                continue;
            result.set(a.key, [...(result.get(a.key) ?? []), b.key]);
            result.set(b.key, [...(result.get(b.key) ?? []), a.key]);
        }
    }
    // Lists come out in input order: for pipeline j, partners i<j are appended
    // while the outer loop is still below j, partners k>j afterwards, both ascending.
    return result;
};
/**
 * The ENABLED pipelines (other than `self`) whose triggers can fire on the same
 * mail as `self.trigger` — `self` is the SAVED pipeline (the editor passes the
 * server copy: the statistics hint this feeds explains the traffic the saved
 * trigger produced, not an unsaved edit) and is checked regardless of its own
 * switch (a disabled pipeline still asks «what else answers this mail»).
 * Input order.
 */
export const overlapPartnersOf = (self, list) => list.filter((p) => p.key !== self.key && p.enabled && triggersOverlap(self.trigger, p.trigger));
//# sourceMappingURL=overlap.js.map